import _ from 'lodash'

import values from 'core/data-model/constants/values'
import { calculatePeriod, calculateWaivePeriod } from 'core/service/basic-plan/period'
import { d } from 'core/service/lib/decimal'
import { isNotNil } from 'core/service/lib/type-check'
import { isRequirePayerInfo, isCheckRiderCampaignDiscountActive } from 'core/service/rider/rider'

const getAgeByPremiumStructure = (rider, initialAge, yearBeginAge) => {
  return {
    value: rider.premiumStructure === values.PREMIUM_LEVEL ? initialAge : yearBeginAge,
    unit: 'year',
  }
}

export const selectAgePayerOrInsured = (rider, initialInsuredAge, yearBeginInsuredAge, payerAge) => {
  if (isRequirePayerInfo(rider.code)) {
    return getAgeByPremiumStructure(rider, payerAge.value, payerAge.value + (yearBeginInsuredAge - initialInsuredAge))
  }

  return getAgeByPremiumStructure(rider, initialInsuredAge, yearBeginInsuredAge)
}

/**
 * createBenefitIllustrationYearEndRiderPremiumService
 * @param {Awaited<ReturnType<import('core/service/rider/rider').createRiderService>>} riderService
 * @param {Awaited<ReturnType<import('core/service/rider/premium-rate').createRiderPremiumRateService>>} riderPremiumService
 */
export function createBenefitIllustrationYearEndRiderPremiumService(riderService, riderPremiumService) {
  const calculateTotalRiderPremium = (
    riderPremiumRatesPayload,
    basicPlan,
    basicPremium,
    initialInsuredAge,
    payerAge,
    payerGender,
    gender,
    occupationFactorForRider,
    modelFactor,
    modelFactorPeriod,
    basicPaymentPeriod,
    basicCoveragePeriod,
    yearBeginInsuredAge,
    isSumYearEnd,
    payerOccupationFactorForRider,
    userAgentType
  ) => (riders) => {
    const yearEnd = yearBeginInsuredAge + 1 - initialInsuredAge
    const insuredAge = { value: initialInsuredAge, unit: 'year' }

    const byWhatNeedsToBePaidOnThisYearEnd = isWithinPaymentPeriod(
      yearEnd,
      insuredAge,
      payerAge,
      basicPaymentPeriod,
      basicCoveragePeriod
    )
    const premiumFactor = isSumYearEnd ? modelFactorPeriod : 1
    const toPremium = (rider) => {
      const riderPremium = calculateRiderPremiumBaseOnPremiumStructure(
        riderPremiumRatesPayload,
        basicPlan,
        basicPremium,
        initialInsuredAge,
        yearBeginInsuredAge,
        payerAge,
        payerGender,
        gender,
        occupationFactorForRider,
        modelFactor,
        modelFactorPeriod,
        payerOccupationFactorForRider,
        userAgentType
      )(rider)

      if (isCheckRiderCampaignDiscountActive(rider, userAgentType)) {
        const applyDiscountPerYear = _.get(rider, 'campaignDiscount.applyDiscountPerYear')
        if (applyDiscountPerYear) {
          return yearEnd <= applyDiscountPerYear
            ? d(_.get(riderPremium, 'premium', 0))
            : d(_.get(riderPremium, 'fullPremium', 0))
        } else {
          return d(_.get(riderPremium, 'premium', 0))
        }
      } else {
        return d(riderPremium)
      }
    }

    return riders
      .filter(byWhatNeedsToBePaidOnThisYearEnd)
      .map(toPremium)
      .reduce((sum, premium) => sum.add(premium), d(0))
      .times(d(premiumFactor))
      .toNumber()
  }

  const calculateRiderPremiumBaseOnPremiumStructure = (
    riderPremiumRatesPayload,
    basicPlan,
    basicPremium,
    initialInsuredAge,
    yearBeginInsuredAge,
    payerAge,
    payerGender,
    gender,
    occupationFactorForRider,
    modelFactor,
    modelFactorPeriod,
    payerOccupationFactorForRider,
    userAgentType
  ) => (rider) => {
    const riderPremiumRates = _.find(riderPremiumRatesPayload, (r) => r.riderCode === rider.code)
    const riderPlan = rider.selectedPlan
    const occupationFactor = occupationFactorForRider[rider.code]
    const payerOccupationFactor = payerOccupationFactorForRider[rider.code]
    const basicYearsOfPayment = calculatePeriod(basicPlan.paymentPeriod, initialInsuredAge)
    const queryRiderPremiumRateDependencies = {
      gender: isRequirePayerInfo(rider.code) ? payerGender : gender,
      selectedBasicPlanCode: basicPlan.basicPlanCode,
      selectedRiderPlanCode: riderPlan.planCode,
      yearsOfCoverage: rider.yearsOfCoverage,
      age: selectAgePayerOrInsured(rider, initialInsuredAge, yearBeginInsuredAge, payerAge),
      occupationFactor,
      basicYearsOfPayment: basicYearsOfPayment,
      waivePeriod: calculateWaivePeriod(rider.code, basicYearsOfPayment, initialInsuredAge),
    }

    const riderPremiumRate = riderPremiumRates
      ? riderService.queryPremiumRate(rider, riderPremiumRates, queryRiderPremiumRateDependencies)
      : 0

    const riders = [rider]
    const riderPremiumRatesObject =
      rider.code === values.CIES
        ? riderPremiumService._getRiderPremiumRates(
            selectAgePayerOrInsured(rider, initialInsuredAge, yearBeginInsuredAge, payerAge),
            payerAge,
            gender,
            payerGender,
            riders,
            basicPlan.productCode,
            occupationFactor,
            riderPremiumRatesPayload,
            basicYearsOfPayment
          )
        : {}

    const allPremiumCalculationDependencies = {
      riderPremiumRate,
      modelFactor,
      riderSumAssured: rider.sumAssured,
      basicPremium,
      occupationFactor,
      modelFactorPeriod,
      payerOccupationFactor,
      userAgentType,
      riderPremiumRates: riderPremiumRatesObject,
    }

    const riderPremium = riderService.calculateRiderPremium(rider, allPremiumCalculationDependencies)
    return isNotNil(riderPremium.fullPremium)
      ? { premium: riderPremium.premium, fullPremium: riderPremium.fullPremium }
      : riderPremium
  }

  const isWithinPaymentPeriod = (yearEnd, age, payerAge, basicPaymentPeriod, basicCoveragePeriod) => (rider) => {
    const riderPlan = rider.selectedPlan

    const yearsOfCoverage = riderService.getYearsOfCoverage(
      rider,
      age,
      payerAge,
      basicPaymentPeriod,
      basicCoveragePeriod,
      riderPlan
    )

    return yearEnd <= yearsOfCoverage
  }

  return {
    calculateTotalRiderPremium,
    isWithinPaymentPeriod,
  }
}
